import type { SurveyResponsePayload } from '@/modules/api';
import {
  FREE_TEXT_QUESTION_TYPES,
  toSingleAnswerValue,
  type Answers,
  type AssessmentQuestion,
} from '@/modules/dynamicAssessments';

type BuildArgs = {
  answers: Answers;
  questions: AssessmentQuestion[];
  userUuid: string;
  questionGroupUri: string;
  programUnitInstanceUri: string;
};

// `FREE_TEXT_QUESTION_TYPES` is the single source of truth shared with
// AssessmentContent's render gate (`text`/`comment`/`number`/`timezone`), so
// the two lists can't drift independently again — they previously did:
// `comment` rendered as free text but routed here as a choice answer. `date`
// is unioned in separately: it's also free-response on the wire (the
// backend's SurveyQuestion.question_type enum has 7 values — choice, tags,
// text, date, number, timezone, likert; `choice`/`tags`/`likert` are the only
// ones that select from a fixed set, so their answer is a surveychoice
// resourceUri) but isn't in the shared set because AssessmentContent renders
// it with its own masked input, not a plain TextField/TextArea.
const isFreeResponseQuestionType = (questionType: string | undefined): boolean =>
  questionType === 'date' || (!!questionType && FREE_TEXT_QUESTION_TYPES.has(questionType));

// Turns the answer map into the /surveycustomerresponse payload (mirrors
// Sparta's AssignedScreenerContainer.onContinue): choice-based answers go in
// `answer` (the choice resource URI), typed-in answers go in `freeResponse`.
export function buildSurveyResponsePayload({
  answers,
  questions,
  userUuid,
  questionGroupUri,
  programUnitInstanceUri,
}: BuildArgs): SurveyResponsePayload {
  return {
    objects: Object.entries(answers).map(([questionUuid, rawValue]) => {
      const questionType = questions.find((q) => q.key === questionUuid)?.questionType;
      const isFreeResponse = isFreeResponseQuestionType(questionType);
      // Legacy assigned screeners are single-select — coerce to a string.
      const value = toSingleAnswerValue(rawValue);
      return {
        user: `/api/v3/user/${userUuid}/`,
        question: `/api/v3/surveyquestion/${questionUuid}/`,
        questionGroup: questionGroupUri,
        answer: isFreeResponse ? null : value,
        freeResponse: isFreeResponse ? value : null,
        programUnitInstance: programUnitInstanceUri,
      };
    }),
  };
}
